import { z } from "zod";

import { stringArrayWithoutNulls } from "@/utils/zod";

import { locationSchema, mediaSchema } from "../packaging-solution";

import { auditEventPreparationTypeSchema } from "./audit-event-type";

export const batchAuditEntryStatuses = [
	"NotStarted",
	"InProgress",
	"Completed",
	"Removed",
	"Failed",
	"Cancelled",
	"CompleteWithExceptions",
] as const;
export const batchAuditEntryStatusSchema = z.enum(batchAuditEntryStatuses);

export const terminalBatchAuditEntryStatuses: ReadonlySet<BatchAuditEntryStatus> = new Set([
	"Completed",
	"CompleteWithExceptions",
	"Removed",
	"Failed",
	"Cancelled",
]);

const batchAuditSearchRequestStatusTypes = ["All", ...batchAuditEntryStatuses] as const;
const batchAuditSearchRequestStatusTypeSchema = z.enum(batchAuditSearchRequestStatusTypes);

const batchAuditSearchRequestFilterTypeSchema = z.enum(["alias", "barcode", "lpn", "productionGroup"]);
export type BatchAuditSearchRequestFilterType = z.infer<typeof batchAuditSearchRequestFilterTypeSchema>;
type BatchAuditSearchRequestFilterInputType = "text" | "select";

export const batchAuditSearchRequestFilterTypes = {
	alias: "Alias",
	barcode: "Barcode",
	lpn: "LPN",
	productionGroup: "Production Group",
} satisfies Record<z.infer<typeof batchAuditSearchRequestFilterTypeSchema>, string>;

export const batchAuditSearchRequestFilterInputTypes = {
	alias: "text",
	barcode: "text",
	lpn: "text",
	productionGroup: "select",
} satisfies Record<BatchAuditSearchRequestFilterType, BatchAuditSearchRequestFilterInputType>;

export const batchAuditEntryPatchRequestSchema = z.object({
	quantity: z.number().optional(),
	alias: z.string().optional(),
});

export const batchAuditEntrySchema = z.object({
	id: z.string(),
	status: batchAuditEntryStatusSchema,

	updatedTimestamp: z.coerce.date(),
	createdTimestamp: z.coerce.date(),

	alias: z.string(),
	licensePlateNumber: z.string().nullish(),
	quantity: z.number(),
	barcodes: stringArrayWithoutNulls,
	location: locationSchema,

	packagingPreparationState: z.object({
		status: z.string(),

		cartonCountPerJob: z.number(),
		labelCountPerJob: z.number(),
		closingCountPerJob: z.number(),
		cartonMedias: z.array(mediaSchema).optional(),
		labelMedias: z.array(mediaSchema).optional(),

		events: z.array(
			z.object({
				auditEntryId: z.string().nonempty(),
				auditType: auditEventPreparationTypeSchema,
				eventUtcTimestamp: z.coerce.date(),
			}),
		),
	}),

	packagingProductionState: z.object({
		inProgressIds: z.array(z.string()),
		completedIds: z.array(z.string()),
		removedIds: z.array(z.string()),

		completedOnMachineGroupIds: z.array(z.string()),
	}),

	packagingClosureState: z.object({
		inProgressIds: z.array(z.string()),
		completedIds: z.array(z.string()),
		removedIds: z.array(z.string()),

		completedOnMachineIds: z.array(z.string()),
	}),

	packagingCompletionState: z.object({
		completedIds: z.array(z.string()),
		removedIds: z.array(z.string()),

		completedOnMachineIds: z.array(z.string()),
		completedOnMachineGroupIds: z.array(z.string()),
	}),
});

export const batchAuditSearchRequestSchema = z
	.object({
		statusType: batchAuditSearchRequestStatusTypeSchema.optional(),
		filterType: batchAuditSearchRequestFilterTypeSchema.optional(),
		filter: z.string().optional(),

		dateRange: z.object({
			startDate: z.string().optional(),
			endDate: z.string().optional(),
		}),

		pagination: z.object({
			pageNumber: z.coerce.number<number>(),
			pageSize: z.coerce.number<number>().min(1),
		}),
	})
	.superRefine((data, context) => {
		const selectedFilterType = data.filterType;
		const needsSelectionFilter =
			selectedFilterType !== undefined && batchAuditSearchRequestFilterInputTypes[selectedFilterType] === "select";
		const hasFilterValue = typeof data.filter === "string" && data.filter.length > 0;

		if (needsSelectionFilter && hasFilterValue === false) {
			const filterTypeLabel =
				selectedFilterType !== undefined ? batchAuditSearchRequestFilterTypes[selectedFilterType] : "Filter";

			context.addIssue({
				code: "custom",
				message: `${filterTypeLabel} filter is required`,
				path: ["filter"],
			});
		}
	});

export const batchAuditSearchResponseSchema = z.object({
	entries: z.array(batchAuditEntrySchema),
	totalNumberOfEntries: z.number(),
});

export const batchRemovalStatusSchema = z.enum(["Deleted", "PartiallyDeleted", "Failed"]);

export const batchRemovalResponseSchema = z.object({
	status: batchRemovalStatusSchema,
});

export const batchDeletionStatusSchema = z.enum(["Deleted", "PartiallyDeleted", "NotFound"]);

export const batchDeletionResponseSchema = z.object({
	status: batchDeletionStatusSchema,
});

export type BatchAuditEntry = z.infer<typeof batchAuditEntrySchema>;
export type BatchAuditEntryStatus = z.infer<typeof batchAuditEntryStatusSchema>;

export type BatchAuditSearchRequest = z.infer<typeof batchAuditSearchRequestSchema>;
export type BatchAuditSearchResponse = z.infer<typeof batchAuditSearchResponseSchema>;

export type BatchAuditEntryPatchRequest = z.infer<typeof batchAuditEntryPatchRequestSchema>;
export type BatchRemovalStatus = z.infer<typeof batchRemovalStatusSchema>;
export type BatchRemovalResponse = z.infer<typeof batchRemovalResponseSchema>;
export type BatchDeletionStatus = z.infer<typeof batchDeletionStatusSchema>;
export type BatchDeletionResponse = z.infer<typeof batchDeletionResponseSchema>;
export type BatchAuditSearchRequestStatusType = z.infer<typeof batchAuditSearchRequestStatusTypeSchema>;
